import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { usePayers } from '../../../context/PayersContext';
import ReactModal from 'react-modal';
import { Button, Input, Textarea, Switch, Typography, Card } from '@material-tailwind/react';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import usePermissions from '../../common/usePermissions';
import ConfirmModal from '../../common/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';
import { parseMpesaExpenseMessage } from './mpesaExpenseMessageParser';
import { CATEGORY_CODE_PATTERN, SUB_CATEGORY_CODE_PATTERN, suggestCategoryCode, suggestSubCategoryCode } from './expenseCodeSuggestions';

const EXPENSE_FUNDING_ACCOUNT_TYPES = ['BANK', 'CASH', 'MPESA'];

const BLANK_LINE_ITEM = () => ({
  expense_type: 'VEHICLE',
  plate_no: '',
  driver_id: '',
  owner_code: '',
  category_id: '',
  sub_category_id: '',
  amount: '',
  description: '',
  _subCategories: [],
});

const ExpenseForm = ({ open, onClose, onSuccess, expense, categories, cars, asPage = false }) => {
  const { token, user } = useAuth();
  const { payers, loading: payersLoading } = usePayers();
  const authToken = token || localStorage.getItem('authToken') || '';

  const [header, setHeader] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    payment_date: new Date().toISOString().split('T')[0],
    payment_ref: '',
    payment_method: 'CASH',
    cashbook_account_id: '',
    paid_by: 'OFFICE',
    supplier_id: null,
    invoice_number: '',
    invoice_date: '',
    notes: '',
    requires_reimbursement: false,
    reimbursement_amount: '',
    is_recurring: false,
    recurring_frequency: '',
    recurring_end_date: '',
  });

  const [lineItems, setLineItems] = useState([BLANK_LINE_ITEM()]);
  const [headerErrors, setHeaderErrors] = useState({});
  const [lineErrors, setLineErrors] = useState([]);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentDateTouched, setPaymentDateTouched] = useState(false);
  const [cashbookAccounts, setCashbookAccounts] = useState([]);
  const [loadingCashbook, setLoadingCashbook] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorQuickAddModal, setVendorQuickAddModal] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddPhone, setQuickAddPhone] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddError, setQuickAddError] = useState('');
  const [pendingParsedVendor, setPendingParsedVendor] = useState(null);
  const [vendorConfirmLoading, setVendorConfirmLoading] = useState(false);
  const [vendorConfirmError, setVendorConfirmError] = useState('');
  // Categories created inline in this form, shown alongside the ones passed in by the page.
  const [extraCategories, setExtraCategories] = useState([]);
  const [categoryModal, setCategoryModal] = useState(null); // { lineIndex, name, code, codeTouched, expenseType, loading, error }
  const [subCategoryModal, setSubCategoryModal] = useState(null); // { lineIndex, name, code, codeTouched, loading, error }

  const allCategories = useMemo(() => [...(categories || []), ...extraCategories], [categories, extraCategories]);

  const { hasPermission } = usePermissions();
  const isApproved = expense?.approval_status === 'APPROVED';
  const canEditApprovedVendor = hasPermission('roles.manage') || hasPermission('expenses.edit_approved_vendor');
  const vendorFieldReadOnly = isApproved && !canEditApprovedVendor;

  const sectionTitleClass = 'mb-4 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500';

  const selectStyles = useMemo(() => ({
    control: (p, s) => ({
      ...p, minHeight: '36px', borderRadius: '0.375rem',
      borderColor: s.isFocused ? '#FF5733' : '#e5e7eb',
      boxShadow: s.isFocused ? '0 0 0 2px rgba(255,87,51,0.1)' : 'none',
      '&:hover': { borderColor: s.isDisabled ? '#e5e7eb' : '#ff6f45' },
      backgroundColor: s.isDisabled ? '#f3f4f6' : '#ffffff', fontSize: '0.8rem',
    }),
    singleValue: (p, s) => ({ ...p, color: s.isDisabled ? '#9ca3af' : '#2a2e30', fontSize: '0.8rem' }),
    menu: (p) => ({ ...p, borderRadius: '0.5rem', boxShadow: '0 6px 18px rgba(15,23,42,0.08)', zIndex: 9999 }),
    menuList: (p) => ({ ...p, maxHeight: '180px' }),
    option: (p, s) => ({ ...p, backgroundColor: s.isFocused ? '#fff7ed' : (s.isSelected ? '#ffedd5' : '#fff'), color: s.isSelected ? '#7c2d12' : '#111827', padding: '6px 10px', fontSize: '0.8rem' }),
    placeholder: (p, s) => ({ ...p, color: s.isDisabled ? '#9ca3af' : '#6b7280', fontSize: '0.8rem' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (p, s) => ({ ...p, color: s.isDisabled ? '#9ca3af' : '#FF5733', padding: '0 6px' }),
    menuPortal: (p) => ({ ...p, zIndex: 9999 }),
  }), []);

  const fetchCashbookAccounts = useCallback(async () => {
    try {
      setLoadingCashbook(true);
      const res = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
        headers: { Authorization: `Bearer ${authToken}` }, params: { is_active: 'true' }
      });
      const raw = res.data?.data || res.data?.accounts || (Array.isArray(res.data) ? res.data : []);
      setCashbookAccounts(raw.filter(a => EXPENSE_FUNDING_ACCOUNT_TYPES.includes(String(a.account_type || '').toUpperCase())));
      // Pre-populate for new expenses only
      if (!expense) {
        try {
          const settingsRes = await axios.get(`${rootUrl}/api/system`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const settings = Array.isArray(settingsRes.data)
            ? settingsRes.data
            : settingsRes.data?.data || [];
          const defaultSetting = settings.find(s => s.name === 'default_expense_cashbook_account_id');
          const defaultId = defaultSetting?.value ? parseInt(defaultSetting.value, 10) : null;
          if (defaultId) {
            setHeader(prev =>
              prev.cashbook_account_id ? prev : { ...prev, cashbook_account_id: String(defaultId) }
            );
          }
        } catch {
          // non-critical, silently ignore
        }
      }
    } catch { setCashbookAccounts([]); }
    finally { setLoadingCashbook(false); }
  }, [authToken, expense]);

  useEffect(() => { if (open || asPage) fetchCashbookAccounts(); }, [open, asPage, fetchCashbookAccounts]);

  useEffect(() => {
    if (!open && !asPage) return;
    axios.get(`${rootUrl}/api/vendors/active`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(res => setVendorOptions((res.data || []).map(v => ({ value: v.id, label: v.name }))))
      .catch(() => {});
  }, [open, asPage, authToken]);

  useEffect(() => {
    if (!expense) {
      setHeader({ expense_date: new Date().toISOString().split('T')[0], payment_date: new Date().toISOString().split('T')[0], payment_ref: '', payment_method: 'CASH', cashbook_account_id: '', paid_by: 'OFFICE', supplier_id: null, invoice_number: '', invoice_date: '', notes: '', requires_reimbursement: false, reimbursement_amount: '', is_recurring: false, recurring_frequency: '', recurring_end_date: '' });
      setLineItems([BLANK_LINE_ITEM()]);
      setSelectedVendor(null);
      setPaymentDateTouched(false);
      setFormError('');
      setCategoryModal(null);
      setSubCategoryModal(null);
      return;
    }
    setHeader({
      expense_date: expense.expense_date ? new Date(expense.expense_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      payment_date: expense.payment_date ? new Date(expense.payment_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      payment_ref: expense.payment_ref || '',
      payment_method: expense.payment_method || 'CASH',
      cashbook_account_id: expense.cashbook_account_id ? String(expense.cashbook_account_id) : '',
      paid_by: expense.paid_by || 'OFFICE',
      supplier_id: expense.supplier_id || null,
      invoice_number: expense.invoice_number || expense.receipt_number || '',
      invoice_date: expense.invoice_date ? new Date(expense.invoice_date).toISOString().split('T')[0] : '',
      notes: expense.notes || '',
      requires_reimbursement: !!expense.requires_reimbursement,
      reimbursement_amount: expense.reimbursement_amount || '',
      is_recurring: !!expense.is_recurring,
      recurring_frequency: expense.recurring_frequency || '',
      recurring_end_date: expense.recurring_end_date ? new Date(expense.recurring_end_date).toISOString().split('T')[0] : '',
    });
    if (expense.supplier_id) setSelectedVendor({ value: expense.supplier_id, label: expense.supplier_name_db || expense.supplier_name || String(expense.supplier_id) });
    if (Array.isArray(expense.line_items) && expense.line_items.length > 0) {
      setLineItems(expense.line_items.map(li => ({ expense_type: li.expense_type || 'VEHICLE', plate_no: li.plate_no || '', driver_id: li.driver_id || '', owner_code: li.owner_code || '', category_id: li.category_id ? String(li.category_id) : '', sub_category_id: li.sub_category_id ? String(li.sub_category_id) : '', amount: li.amount ? String(li.amount) : '', description: li.description || '', _subCategories: [] })));
    } else {
      setLineItems([{ expense_type: expense.expense_type || 'VEHICLE', plate_no: expense.plate_no || '', driver_id: expense.driver_id ? String(expense.driver_id) : '', owner_code: expense.owner_code || '', category_id: expense.category_id ? String(expense.category_id) : '', sub_category_id: expense.sub_category_id ? String(expense.sub_category_id) : '', amount: expense.amount ? String(expense.amount) : '', description: expense.description || '', _subCategories: [] }]);
    }
    setFormError('');
  }, [expense]);

  const fetchSubCategoriesForRow = useCallback(async (index, categoryId) => {
    if (!categoryId) return;
    try {
      const res = await axios.get(`${rootUrl}/api/expenses/sub-categories`, { headers: { Authorization: `Bearer ${authToken}` }, params: { category_id: categoryId } });
      if (res.data.success) setLineItems(prev => prev.map((item, i) => i === index ? { ...item, _subCategories: res.data.data || [] } : item));
    } catch { /* non-critical */ }
  }, [authToken]);

  const updateLine = useCallback((index, field, value) => {
    setLineItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, [field]: value };
      if (field === 'expense_type' && value !== 'VEHICLE') { updated.plate_no = ''; updated.driver_id = ''; updated.owner_code = ''; }
      if (field === 'category_id') { updated.sub_category_id = ''; updated._subCategories = []; }
      return updated;
    }));
    if (field === 'category_id' && value) fetchSubCategoriesForRow(index, value);
    setLineErrors(prev => { const next = [...prev]; if (next[index]) { const e = { ...next[index] }; delete e[field]; next[index] = e; } return next; });
    if (formError) setFormError('');
  }, [fetchSubCategoriesForRow, formError]);

  const selectVehicleForRow = useCallback((index, plateNo) => {
    const car = (cars || []).find(c => c.plate_no === plateNo);
    setLineItems(prev => prev.map((item, i) => i !== index ? item : { ...item, plate_no: plateNo, driver_id: car?.driver_id ? String(car.driver_id) : '', owner_code: car?.owner_code || '' }));
    setLineErrors(prev => { const next = [...prev]; if (next[index]) { const e = { ...next[index] }; delete e.plate_no; next[index] = e; } return next; });
  }, [cars]);

  const addLine = () => setLineItems(prev => [...prev, BLANK_LINE_ITEM()]);
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); setLineErrors(prev => prev.filter((_, i) => i !== index)); };

  const runningTotal = lineItems.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);

  const updateHeader = (field, value) => {
    setHeader(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'expense_date' && !paymentDateTouched) next.payment_date = value;
      if (field === 'requires_reimbursement' && value) next.reimbursement_amount = String(runningTotal || '');
      return next;
    });
    setHeaderErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    if (field === 'payment_date') setPaymentDateTouched(true);
    if (formError) setFormError('');
  };

  const normalizeVendorName = (value) => String(value || '').replace(/\s+/g, ' ').trim().toUpperCase();

  const applyMpesaPaste = (parsed) => {
    setHeader(prev => ({
      ...prev,
      payment_ref: parsed.reference,
      payment_method: parsed.paymentMethod,
      expense_date: parsed.transactionDate,
      payment_date: parsed.transactionDate,
      notes: parsed.notes
    }));
    setPaymentDateTouched(true);
    setLineItems(prev => {
      const next = prev.length > 0 ? [...prev] : [BLANK_LINE_ITEM()];
      next[0] = { ...next[0], amount: parsed.amount, description: parsed.payeeName };
      return next;
    });
    const existingVendor = vendorOptions.find(v => normalizeVendorName(v.label) === normalizeVendorName(parsed.payeeName));
    if (existingVendor) {
      setSelectedVendor(existingVendor);
      setHeader(prev => ({ ...prev, supplier_id: existingVendor.value }));
    } else {
      setPendingParsedVendor(parsed.payeeName);
      setVendorConfirmError('');
    }
  };

  const handleNotesPaste = (event) => {
    const pastedText = event.clipboardData?.getData('text') || '';
    const parsed = parseMpesaExpenseMessage(pastedText);
    if (!parsed) return;
    event.preventDefault();
    applyMpesaPaste(parsed);
  };

  const handleNotesChange = (event) => {
    updateHeader('notes', event.target.value);
    const parsed = parseMpesaExpenseMessage(event.target.value);
    if (parsed) applyMpesaPaste(parsed);
  };

  const createPendingParsedVendor = async () => {
    if (!pendingParsedVendor) return;
    setVendorConfirmLoading(true);
    setVendorConfirmError('');
    try {
      const res = await axios.post(
        `${rootUrl}/api/vendors`,
        {
          name: pendingParsedVendor,
          phone: null,
          input_by: user?.username || user?.name || 'system'
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const createdVendor = res.data?.data || res.data;
      const newOpt = { value: createdVendor.id, label: createdVendor.name };
      setVendorOptions(prev => [...prev, newOpt]);
      setSelectedVendor(newOpt);
      updateHeader('supplier_id', newOpt.value);
      setPendingParsedVendor(null);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || '';
      setVendorConfirmError(msg.includes('23505') ? 'A vendor with this name may already exist.' : 'Failed to create vendor.');
    } finally {
      setVendorConfirmLoading(false);
    }
  };

  const anyLineRequiresVendor = lineItems.some(li => {
    const cat = allCategories.find(c => String(c.category_id) === String(li.category_id));
    return !!cat?.requires_vendor;
  });

  const quickAddErrorMessage = (err, kind) => {
    const data = err.response?.data || {};
    if (data.errors && typeof data.errors === 'object') return Object.values(data.errors).join(', ');
    const msg = data.message || data.error || '';
    if (/already exists|23505/i.test(msg)) return `That ${kind} code is already in use. Change the code and try again.`;
    return msg || `Failed to create ${kind}.`;
  };

  const openCategoryModal = (lineIndex) => {
    setCategoryModal({ lineIndex, name: '', code: '', codeTouched: false, expenseType: lineItems[lineIndex]?.expense_type || 'VEHICLE', loading: false, error: '' });
  };

  const updateCategoryModal = (field, value) => {
    setCategoryModal(prev => {
      if (!prev) return prev;
      if (field === 'name') {
        const name = value.toUpperCase();
        const code = prev.codeTouched ? prev.code : suggestCategoryCode(name, allCategories.map(c => c.code));
        return { ...prev, name, code, error: '' };
      }
      if (field === 'code') return { ...prev, code: value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3), codeTouched: true, error: '' };
      return { ...prev, [field]: value, error: '' };
    });
  };

  const handleCreateCategory = async () => {
    const modal = categoryModal;
    if (!modal) return;
    const name = modal.name.trim();
    if (!name) { setCategoryModal(prev => ({ ...prev, error: 'Category name is required.' })); return; }
    if (!CATEGORY_CODE_PATTERN.test(modal.code)) { setCategoryModal(prev => ({ ...prev, error: 'Code must be exactly 3 letters, e.g. FUE.' })); return; }

    setCategoryModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await axios.post(`${rootUrl}/api/expenses/categories`, {
        code: modal.code,
        name,
        is_vehicle_related: modal.expenseType === 'VEHICLE',
        is_office_related: modal.expenseType === 'OFFICE',
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      const row = res.data?.data;
      if (!row?.id) throw new Error('Unexpected response');

      setExtraCategories(prev => [...prev, {
        category_id: row.id,
        category_name: row.name,
        code: row.code,
        expense_type: modal.expenseType,
        requires_vendor: !!row.requires_vendor,
      }]);
      if (lineItems[modal.lineIndex]?.expense_type !== modal.expenseType) updateLine(modal.lineIndex, 'expense_type', modal.expenseType);
      updateLine(modal.lineIndex, 'category_id', String(row.id));
      setCategoryModal(null);
    } catch (err) {
      setCategoryModal(prev => prev && ({ ...prev, loading: false, error: quickAddErrorMessage(err, 'category') }));
    }
  };

  const openSubCategoryModal = (lineIndex) => {
    if (!lineItems[lineIndex]?.category_id) return;
    setSubCategoryModal({ lineIndex, name: '', code: '', codeTouched: false, loading: false, error: '' });
  };

  const updateSubCategoryModal = (field, value) => {
    setSubCategoryModal(prev => {
      if (!prev) return prev;
      if (field === 'name') {
        const name = value.toUpperCase();
        const line = lineItems[prev.lineIndex];
        const category = allCategories.find(c => String(c.category_id) === String(line?.category_id));
        const code = prev.codeTouched ? prev.code : suggestSubCategoryCode(category, name, (line?._subCategories || []).map(sc => sc.code));
        return { ...prev, name, code, error: '' };
      }
      if (field === 'code') {
        const letters = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6);
        return { ...prev, code: letters.length > 3 ? `${letters.slice(0, 3)}-${letters.slice(3)}` : letters, codeTouched: true, error: '' };
      }
      return { ...prev, [field]: value, error: '' };
    });
  };

  const handleCreateSubCategory = async () => {
    const modal = subCategoryModal;
    if (!modal) return;
    const line = lineItems[modal.lineIndex];
    const name = modal.name.trim();
    if (!line?.category_id) { setSubCategoryModal(prev => ({ ...prev, error: 'Select a category first.' })); return; }
    if (!name) { setSubCategoryModal(prev => ({ ...prev, error: 'Sub-category name is required.' })); return; }
    if (!SUB_CATEGORY_CODE_PATTERN.test(modal.code)) { setSubCategoryModal(prev => ({ ...prev, error: 'Code must look like ABC-XYZ (3 letters, dash, 3 letters).' })); return; }

    setSubCategoryModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await axios.post(`${rootUrl}/api/expenses/sub-categories`, {
        category_id: parseInt(line.category_id, 10),
        code: modal.code,
        name,
        username: user?.username || user?.name || 'system',
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      const row = res.data?.data;
      if (!row?.id) throw new Error('Unexpected response');

      setLineItems(prev => prev.map((item, i) => i !== modal.lineIndex ? item : {
        ...item,
        _subCategories: [...(item._subCategories || []), row],
        sub_category_id: String(row.id),
      }));
      setLineErrors(prev => { const next = [...prev]; if (next[modal.lineIndex]) { const e = { ...next[modal.lineIndex] }; delete e.sub_category_id; next[modal.lineIndex] = e; } return next; });
      setSubCategoryModal(null);
    } catch (err) {
      setSubCategoryModal(prev => prev && ({ ...prev, loading: false, error: quickAddErrorMessage(err, 'sub-category') }));
    }
  };

  const validate = () => {
    const hErr = {};
    if (!header.expense_date) hErr.expense_date = 'Expense date is required';
    if (!header.payment_ref || !String(header.payment_ref).trim()) hErr.payment_ref = 'Payment reference is required';
    if (!header.cashbook_account_id) hErr.cashbook_account_id = 'Funding account is required';
    if (anyLineRequiresVendor && !header.supplier_id) hErr.supplier_id = 'A vendor is required for one or more selected categories';
    if (header.requires_reimbursement && (!header.reimbursement_amount || parseFloat(header.reimbursement_amount) <= 0)) hErr.reimbursement_amount = 'Reimbursement amount is required';
    const lErrs = lineItems.map(li => {
      const e = {};
      if (!li.category_id) e.category_id = 'Category is required';
      if (!li.amount || parseFloat(li.amount) <= 0) e.amount = 'Amount must be > 0';
      if (!li.description || !li.description.trim()) e.description = 'Description is required';
      if (li.expense_type === 'VEHICLE' && !li.plate_no) e.plate_no = 'Vehicle is required';
      if (li._subCategories.length > 0 && !li.sub_category_id) e.sub_category_id = 'Sub-category is required';
      return e;
    });
    setHeaderErrors(hErr);
    setLineErrors(lErrs);
    return Object.keys(hErr).length === 0 && lErrs.every(e => Object.keys(e).length === 0);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const expenseId = expense?.expense_id ?? expense?.id;
      const endpoint = expense ? `${rootUrl}/api/expenses/${expenseId}` : `${rootUrl}/api/expenses`;
      const method = expense ? 'put' : 'post';
      const payload = {
        ...header,
        ...(method === 'post' ? { input_by: user?.username || user?.name || 'system' } : { username: user?.username || user?.name || 'system' }),
        invoice_number: String(header.invoice_number || '').trim() || null,
        invoice_date: header.invoice_date || null,
        line_items: lineItems.map(li => ({ expense_type: li.expense_type, plate_no: li.plate_no || null, driver_id: li.driver_id || null, owner_code: li.owner_code || null, category_id: parseInt(li.category_id), sub_category_id: li.sub_category_id ? parseInt(li.sub_category_id) : null, amount: parseFloat(li.amount), description: li.description })),
      };
      const res = await axios[method](endpoint, payload, { headers: { Authorization: `Bearer ${authToken}` } });
      if (res.data.success) onSuccess?.(res.data.data || null);
    } catch (error) {
      const serverMessage = error.response?.data?.message || error.response?.data?.error || '';
      if (serverMessage && /no week|week not found|previous week/i.test(serverMessage)) {
        setFormError('Cannot post expense: the previous week has not been closed.');
      } else if (error.response?.data?.errors && typeof error.response.data.errors === 'object') {
        setFormError(Object.values(error.response.data.errors).join(', '));
      } else if (serverMessage) {
        setFormError(serverMessage);
      } else {
        setFormError('Failed to save expense');
      }
    } finally { setLoading(false); }
  };

  const paymentMethodOptions = [
    { label: 'Cash', value: 'CASH' }, { label: 'M-Pesa', value: 'MPESA' },
    { label: 'Bank Transfer', value: 'BANK_TRANSFER' }, { label: 'Cheque', value: 'CHEQUE' },
    { label: 'Card', value: 'CARD' }, { label: 'Other', value: 'OTHER' },
  ];
  const typeOptions = [{ label: 'Vehicle', value: 'VEHICLE' }, { label: 'Office', value: 'OFFICE' }];
  const carOptions = (cars || []).map(c => ({ label: c.plate_no, value: c.plate_no }));
  const quickAddButtonClass = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded border border-dashed border-[#FF5733] bg-orange-50 text-[#FF5733] hover:bg-orange-100 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-300 transition-colors';
  const subCategoryModalCategory = subCategoryModal
    ? allCategories.find(c => String(c.category_id) === String(lineItems[subCategoryModal.lineIndex]?.category_id))
    : null;

  const modalContent = (
    <>
      <div className={`border-b border-gray-200 ${asPage ? 'bg-white px-6 sm:px-8 py-5' : 'bg-[#fff8f5] px-6 py-4'}`}>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#FF5733]">Expense Form</p>
        <h1 className="mt-2 text-2xl font-bold text-[#2a2e30] md:text-3xl">{expense ? 'Edit Expense' : 'Add New Expense'}</h1>
        <p className="mt-2 text-sm text-gray-600">{expense ? expense.expense_no || 'Update the existing expense record' : 'Create a new expense record'}</p>
      </div>

      <div className={asPage ? 'px-6 sm:px-8 py-6 bg-white' : 'overflow-y-auto max-h-[calc(95vh-180px)] px-6 py-6 bg-[#fff8f5]'}>
        <div className="space-y-6">
          {formError && <div className="p-3 rounded-sm bg-red-50 border border-red-100"><Typography variant="small" className="text-red-500">{formError}</Typography></div>}

          {/* PAYMENT DETAILS */}
          <Card className="p-5 shadow-none rounded-sm border border-gray-200 bg-white">
            <Typography variant="small" className={sectionTitleClass}>Payment Details</Typography>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Expense Date *</Typography>
                <Input type="date" value={header.expense_date} onChange={e => updateHeader('expense_date', e.target.value)} error={!!headerErrors.expense_date} />
                {headerErrors.expense_date && <Typography variant="small" className="mt-1 text-red-500">{headerErrors.expense_date}</Typography>}
              </div>
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Payment Reference *</Typography>
                <Input value={header.payment_ref} onChange={e => updateHeader('payment_ref', e.target.value)} error={!!headerErrors.payment_ref} placeholder="e.g. QHK2LMNOP" />
                {headerErrors.payment_ref && <Typography variant="small" className="mt-1 text-red-500">{headerErrors.payment_ref}</Typography>}
              </div>
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Payment Date</Typography>
                <Input type="date" value={header.payment_date} onChange={e => updateHeader('payment_date', e.target.value)} />
              </div>
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Payment Method</Typography>
                <ReactSelect options={paymentMethodOptions} value={paymentMethodOptions.find(o => o.value === header.payment_method)} onChange={o => updateHeader('payment_method', o?.value || '')} isSearchable={false} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
              </div>
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Funding Account *</Typography>
                <ReactSelect
                  options={cashbookAccounts.map(a => ({ label: `${a.account_name} (${a.account_type})`, value: String(a.id) }))}
                  value={cashbookAccounts.map(a => ({ value: String(a.id), label: `${a.account_name} (${a.account_type})` })).find(o => o.value === String(header.cashbook_account_id)) || null}
                  onChange={o => updateHeader('cashbook_account_id', o?.value || '')}
                  isLoading={loadingCashbook} isSearchable styles={selectStyles} placeholder={loadingCashbook ? 'Loading…' : 'Select account'}
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
                {headerErrors.cashbook_account_id && <Typography variant="small" className="mt-1 text-red-500">{headerErrors.cashbook_account_id}</Typography>}
              </div>
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Paid By</Typography>
                <ReactSelect
                  options={payers.map(p => ({ label: p.name, value: p.code }))}
                  value={payers.find(p => p.code === header.paid_by) ? { label: payers.find(p => p.code === header.paid_by).name, value: header.paid_by } : null}
                  onChange={o => updateHeader('paid_by', o?.value || '')}
                  isDisabled={payersLoading} isSearchable={false} styles={selectStyles}
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>
            </div>
          </Card>

          {/* VENDOR / INVOICE */}
          <Card className="p-5 shadow-none rounded-sm border border-gray-200 bg-white">
            <Typography variant="small" className={sectionTitleClass}>Vendor / Invoice</Typography>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Vendor{anyLineRequiresVendor && <span className="text-red-500 ml-0.5">*</span>}</Typography>
                {vendorFieldReadOnly ? (
                  <p className="text-sm text-gray-700">{selectedVendor?.label || '—'}</p>
                ) : (
                  <CreatableSelect isClearable options={vendorOptions} value={selectedVendor}
                    onChange={opt => { setSelectedVendor(opt); updateHeader('supplier_id', opt?.value ?? null); }}
                    onCreateOption={val => { setQuickAddName(val.toUpperCase()); setQuickAddPhone(''); setQuickAddError(''); setVendorQuickAddModal(true); }}
                    placeholder="Search or create vendor…" formatCreateLabel={val => `＋ Create "${val.toUpperCase()}"`}
                    styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                  />
                )}
                {headerErrors.supplier_id && <p className="text-red-500 text-xs mt-1">{headerErrors.supplier_id}</p>}
              </div>
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Invoice Number</Typography>
                <Input value={header.invoice_number} onChange={e => updateHeader('invoice_number', e.target.value)} placeholder="Optional" />
              </div>
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Invoice Date</Typography>
                <Input type="date" value={header.invoice_date} onChange={e => updateHeader('invoice_date', e.target.value)} />
              </div>
            </div>
          </Card>

          {/* LINE ITEMS */}
          <Card className="p-5 shadow-none rounded-sm border border-gray-200 bg-white">
            <div className="flex items-center justify-between mb-4">
              <Typography variant="small" className={sectionTitleClass.replace('mb-4', '')}>Expense Line Items</Typography>
              <Typography variant="small" className="text-gray-500">{lineItems.length} item{lineItems.length !== 1 ? 's' : ''} · Total: <span className="font-bold text-[#FF5733]">KES {runningTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></Typography>
            </div>
            <div className="space-y-3">
              {lineItems.map((li, idx) => {
                const rowErr = lineErrors[idx] || {};
                const filteredCats = allCategories.filter(c => c.expense_type === li.expense_type).map(c => ({ label: c.category_name, value: String(c.category_id) }));
                const subCatOpts = li._subCategories.map(s => ({ label: s.name, value: String(s.id) }));
                return (
                  <div key={idx} data-testid={`expense-line-item-${idx}`} className="rounded-sm border border-gray-200 bg-gray-50/60 p-3 sm:p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <Typography variant="small" className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">Line {idx + 1}</Typography>
                      <button onClick={() => removeLine(idx)} disabled={lineItems.length <= 1} className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-gray-300 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-30 transition-colors" title="Remove line">
                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
                      <div className="min-w-0">
                        <Typography variant="small" color="blue-gray" className="mb-1 text-xs font-medium">Type</Typography>
                        <ReactSelect options={typeOptions} value={typeOptions.find(o => o.value === li.expense_type)} onChange={o => updateLine(idx, 'expense_type', o?.value || 'VEHICLE')} isSearchable={false} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
                      </div>
                      <div className="min-w-0">
                        <Typography variant="small" color="blue-gray" className="mb-1 text-xs font-medium">Vehicle</Typography>
                        {li.expense_type === 'VEHICLE' ? (
                          <>
                            <ReactSelect options={carOptions} value={carOptions.find(o => o.value === li.plate_no) || null} onChange={o => selectVehicleForRow(idx, o?.value || '')} isSearchable styles={selectStyles} placeholder="Plate…" menuPortalTarget={document.body} menuPosition="fixed" />
                            {rowErr.plate_no && <p className="text-red-500 text-[10px] mt-0.5">{rowErr.plate_no}</p>}
                          </>
                        ) : <div className="flex h-9 items-center rounded border border-gray-200 bg-white px-2 text-xs text-gray-400">N/A</div>}
                      </div>
                      <div className="min-w-0">
                        <Typography variant="small" color="blue-gray" className="mb-1 text-xs font-medium">Category</Typography>
                        <div className="flex items-start gap-1.5">
                          <div className="min-w-0 flex-1">
                            <ReactSelect options={filteredCats} value={filteredCats.find(o => o.value === String(li.category_id)) || null} onChange={o => updateLine(idx, 'category_id', o?.value || '')} isSearchable styles={selectStyles} placeholder="Category…" menuPortalTarget={document.body} menuPosition="fixed" />
                          </div>
                          <button type="button" onClick={() => openCategoryModal(idx)} className={quickAddButtonClass} title="Create a new category" aria-label={`Create a new category for line ${idx + 1}`}>
                            <FontAwesomeIcon icon={faPlus} className="text-xs" />
                          </button>
                        </div>
                        {rowErr.category_id && <p className="text-red-500 text-[10px] mt-0.5">{rowErr.category_id}</p>}
                      </div>
                      <div className="min-w-0">
                        <Typography variant="small" color="blue-gray" className="mb-1 text-xs font-medium">Sub-category</Typography>
                        <div className="flex items-start gap-1.5">
                          <div className="min-w-0 flex-1">
                            <ReactSelect options={subCatOpts} value={subCatOpts.find(o => o.value === String(li.sub_category_id)) || null} onChange={o => updateLine(idx, 'sub_category_id', o?.value || '')} isSearchable styles={selectStyles} placeholder={subCatOpts.length === 0 ? 'None' : 'Sub-cat…'} isDisabled={!li.category_id || subCatOpts.length === 0} menuPortalTarget={document.body} menuPosition="fixed" />
                          </div>
                          <button type="button" onClick={() => openSubCategoryModal(idx)} disabled={!li.category_id} className={quickAddButtonClass} title={li.category_id ? 'Create a new sub-category' : 'Select a category first'} aria-label={`Create a new sub-category for line ${idx + 1}`}>
                            <FontAwesomeIcon icon={faPlus} className="text-xs" />
                          </button>
                        </div>
                        {rowErr.sub_category_id && <p className="text-red-500 text-[10px] mt-0.5">{rowErr.sub_category_id}</p>}
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[180px_minmax(0,1fr)]">
                      <div className="min-w-0">
                        <Typography variant="small" color="blue-gray" className="mb-1 text-xs font-medium">Amount (KES)</Typography>
                        <input type="number" value={li.amount} onChange={e => updateLine(idx, 'amount', e.target.value)} className={`h-9 w-full rounded border bg-white px-2 text-right text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5733] ${rowErr.amount ? 'border-red-400' : 'border-gray-200'}`} placeholder="0.00" />
                        {rowErr.amount && <p className="text-red-500 text-[10px] mt-0.5">{rowErr.amount}</p>}
                      </div>
                      <div className="min-w-0">
                        <Typography variant="small" color="blue-gray" className="mb-1 text-xs font-medium">Description</Typography>
                        <input value={li.description} onChange={e => updateLine(idx, 'description', e.target.value)} className={`h-9 w-full rounded border bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5733] ${rowErr.description ? 'border-red-400' : 'border-gray-200'}`} placeholder="Description…" />
                        {rowErr.description && <p className="text-red-500 text-[10px] mt-0.5">{rowErr.description}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button onClick={addLine} className="flex items-center gap-2 text-xs text-[#FF5733] bg-orange-50 border border-dashed border-[#FF5733] rounded px-3 py-1.5 hover:bg-orange-100 font-semibold transition-colors">
                <FontAwesomeIcon icon={faPlus} />Add line item
              </button>
              <span className="text-[10px] text-gray-400">Min. 1 line item required</span>
            </div>
          </Card>

          {/* NOTES & OPTIONS */}
          <Card className="p-5 shadow-none rounded-sm border border-gray-200 bg-white">
            <Typography variant="small" className={sectionTitleClass}>Notes & Additional Options</Typography>
            <div className="space-y-4">
              <div className="flex flex-col">
                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Notes</Typography>
                <Textarea value={header.notes} onChange={handleNotesChange} onPaste={handleNotesPaste} rows={2} placeholder="Optional" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <Typography variant="small" className="text-gray-700 font-medium mb-1">Requires Reimbursement</Typography>
                    <Typography variant="small" className="text-gray-500 text-xs">Applied to total amount</Typography>
                  </div>
                  <Switch checked={header.requires_reimbursement} onChange={e => updateHeader('requires_reimbursement', e.target.checked)} />
                </div>
                {header.requires_reimbursement && (
                  <div className="flex flex-col p-3 bg-white rounded-lg border border-gray-200">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Reimbursement Amount (KES)</Typography>
                    <Input type="number" value={header.reimbursement_amount} onChange={e => updateHeader('reimbursement_amount', e.target.value)} className="text-right" />
                    {headerErrors.reimbursement_amount && <Typography variant="small" className="mt-1 text-red-500">{headerErrors.reimbursement_amount}</Typography>}
                  </div>
                )}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <Typography variant="small" className="text-gray-700 font-medium mb-1">Recurring Expense</Typography>
                    <Typography variant="small" className="text-gray-500 text-xs">Applies to whole transaction</Typography>
                  </div>
                  <Switch checked={header.is_recurring} onChange={e => updateHeader('is_recurring', e.target.checked)} />
                </div>
              </div>
              {header.is_recurring && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                  <div className="flex flex-col">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Recurring Frequency *</Typography>
                    <ReactSelect
                      options={[{ label: 'Weekly', value: 'WEEKLY' }, { label: 'Monthly', value: 'MONTHLY' }, { label: 'Quarterly', value: 'QUARTERLY' }, { label: 'Yearly', value: 'YEARLY' }]}
                      value={[{ label: 'Weekly', value: 'WEEKLY' }, { label: 'Monthly', value: 'MONTHLY' }, { label: 'Quarterly', value: 'QUARTERLY' }, { label: 'Yearly', value: 'YEARLY' }].find(o => o.value === header.recurring_frequency) || null}
                      onChange={o => updateHeader('recurring_frequency', o?.value || '')}
                      isSearchable={false} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">Recurring End Date</Typography>
                    <Input type="date" value={header.recurring_end_date} onChange={e => updateHeader('recurring_end_date', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <div className="flex items-center justify-end shrink-0 flex-wrap border-t border-gray-200 bg-white px-6 py-4 gap-3">
        <Button variant="outlined" onClick={onClose} disabled={loading} className="flex items-center gap-2">
          <FontAwesomeIcon icon={faTimes} />Cancel
        </Button>
        <Button className="bg-[#FF5733] hover:bg-[#ff4520] flex items-center gap-2" onClick={handleSubmit} disabled={loading}>
          <FontAwesomeIcon icon={faSave} />
          {loading ? 'Saving…' : expense ? 'Update Expense' : 'Save Expense'}
        </Button>
      </div>

      {vendorQuickAddModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-semibold text-gray-800 mb-4">Add New Vendor</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Name</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase" value={quickAddName} onChange={e => setQuickAddName(e.target.value.toUpperCase())} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Phone</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={quickAddPhone} onChange={e => setQuickAddPhone(e.target.value)} placeholder="0712345678" maxLength={10} />
              </div>
              {quickAddError && <p className="text-red-500 text-xs">{quickAddError}</p>}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button className="text-sm text-gray-500 hover:text-gray-700" onClick={() => setVendorQuickAddModal(false)} disabled={quickAddLoading}>Cancel</button>
              <button
                className="bg-[#FF5733] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#e64520] disabled:opacity-50"
                disabled={quickAddLoading || !quickAddName.trim() || !/^\d{10}$/.test(quickAddPhone)}
                onClick={async () => {
                  setQuickAddLoading(true); setQuickAddError('');
                  try {
                    const res = await axios.post(`${rootUrl}/api/vendors`, { name: quickAddName, phone: quickAddPhone, input_by: user?.username || user?.name || 'system' }, { headers: { Authorization: `Bearer ${authToken}` } });
                    const nv = res.data;
                    const newOpt = { value: nv.id, label: nv.name };
                    setVendorOptions(prev => [...prev, newOpt]);
                    setSelectedVendor(newOpt);
                    updateHeader('supplier_id', nv.id);
                    setVendorQuickAddModal(false);
                  } catch (err) {
                    const msg = err.response?.data?.error || err.response?.data?.message || '';
                    setQuickAddError(msg.includes('23505') ? 'A vendor with this name may already exist.' : 'Failed to create vendor.');
                  } finally { setQuickAddLoading(false); }
                }}
              >{quickAddLoading ? 'Saving…' : 'Save Vendor'}</button>
            </div>
          </div>
        </div>
      )}

      {categoryModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-labelledby="category-quick-add-title">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 id="category-quick-add-title" className="font-semibold text-gray-800 mb-4">Add New Category</h3>
            <div className="space-y-3">
              <div>
                <label htmlFor="quick-add-category-name" className="text-sm font-medium text-gray-700 block mb-1">Category Name</label>
                <input id="quick-add-category-name" autoFocus className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase" value={categoryModal.name} onChange={e => updateCategoryModal('name', e.target.value)} placeholder="e.g. FUEL" />
              </div>
              <div>
                <label htmlFor="quick-add-category-code" className="text-sm font-medium text-gray-700 block mb-1">Code</label>
                <input id="quick-add-category-code" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase font-mono" value={categoryModal.code} onChange={e => updateCategoryModal('code', e.target.value)} placeholder="ABC" maxLength={3} />
                <p className="mt-1 text-xs text-gray-500">3 letters, suggested from the name. Must be unique.</p>
              </div>
              <div>
                <label htmlFor="quick-add-category-type" className="text-sm font-medium text-gray-700 block mb-1">Expense Type</label>
                <ReactSelect inputId="quick-add-category-type" options={typeOptions} value={typeOptions.find(o => o.value === categoryModal.expenseType)} onChange={o => updateCategoryModal('expenseType', o?.value || 'VEHICLE')} isSearchable={false} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
              </div>
              {categoryModal.error && <p className="text-red-500 text-xs">{categoryModal.error}</p>}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" className="text-sm text-gray-500 hover:text-gray-700" onClick={() => setCategoryModal(null)}>Cancel</button>
              <button
                type="button"
                className="bg-[#FF5733] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#e64520] disabled:opacity-50"
                disabled={categoryModal.loading || !categoryModal.name.trim() || !CATEGORY_CODE_PATTERN.test(categoryModal.code)}
                onClick={handleCreateCategory}
              >{categoryModal.loading ? 'Saving…' : 'Save Category'}</button>
            </div>
          </div>
        </div>
      )}

      {subCategoryModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-labelledby="subcategory-quick-add-title">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 id="subcategory-quick-add-title" className="font-semibold text-gray-800 mb-1">Add New Sub-category</h3>
            <p className="mb-4 text-xs text-gray-500">Under <span className="font-semibold text-gray-700">{subCategoryModalCategory?.category_name || '—'}</span>{subCategoryModalCategory?.code ? ` (${subCategoryModalCategory.code})` : ''}</p>
            <div className="space-y-3">
              <div>
                <label htmlFor="quick-add-subcategory-name" className="text-sm font-medium text-gray-700 block mb-1">Sub-category Name</label>
                <input id="quick-add-subcategory-name" autoFocus className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase" value={subCategoryModal.name} onChange={e => updateSubCategoryModal('name', e.target.value)} placeholder="e.g. SUPER 95" />
              </div>
              <div>
                <label htmlFor="quick-add-subcategory-code" className="text-sm font-medium text-gray-700 block mb-1">Code</label>
                <input id="quick-add-subcategory-code" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase font-mono" value={subCategoryModal.code} onChange={e => updateSubCategoryModal('code', e.target.value)} placeholder="ABC-XYZ" maxLength={7} />
                <p className="mt-1 text-xs text-gray-500">Format ABC-XYZ: category code, then 3 letters from the name. Must be unique.</p>
              </div>
              {subCategoryModal.error && <p className="text-red-500 text-xs">{subCategoryModal.error}</p>}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" className="text-sm text-gray-500 hover:text-gray-700" onClick={() => setSubCategoryModal(null)}>Cancel</button>
              <button
                type="button"
                className="bg-[#FF5733] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#e64520] disabled:opacity-50"
                disabled={subCategoryModal.loading || !subCategoryModal.name.trim() || !SUB_CATEGORY_CODE_PATTERN.test(subCategoryModal.code)}
                onClick={handleCreateSubCategory}
              >{subCategoryModal.loading ? 'Saving…' : 'Save Sub-category'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!pendingParsedVendor}
        title="Create vendor from M-PESA?"
        description={`No existing vendor matched "${pendingParsedVendor}". Create and select this vendor?`}
        confirmText="Create Vendor"
        cancelText="Leave Blank"
        variant="primary"
        loading={vendorConfirmLoading}
        error={vendorConfirmError}
        onCancel={() => { setPendingParsedVendor(null); setVendorConfirmError(''); }}
        onConfirm={createPendingParsedVendor}
      />
    </>
  );

  if (asPage) return <div className="bg-white shadow-lg rounded-xl border border-gray-100 overflow-hidden">{modalContent}</div>;

  return (
    <ReactModal isOpen={open} onRequestClose={onClose} contentLabel={expense ? 'Edit Expense' : 'Add New Expense'}
      className="relative bg-white m-4 rounded-lg max-w-[95%] md:max-w-[90%] 2xl:max-w-[80%] max-h-[95vh] overflow-hidden focus:outline-none"
      overlayClassName="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4"
      appElement={document.getElementById('root') || document.body}
    >{modalContent}</ReactModal>
  );
};

export default ExpenseForm;
